import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Modal, Select, Input, Button, message as antdMessage } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';

const ROLE_PRESETS = ['system', 'user', 'assistant', 'tool'];
const CUSTOM = '__custom__';

function blankRow() {
    return { role: 'system', content: '', roleMode: 'system' };
}

function toDraftRows(messages) {
    if (!Array.isArray(messages) || !messages.length) return [blankRow()];
    return messages.map((m) => {
        const role = String(m.role || 'system');
        const isPreset = ROLE_PRESETS.includes(role);
        return {
            role: isPreset ? role : role,
            content: String(m.content || ''),
            roleMode: isPreset ? role : CUSTOM,
            customRole: isPreset ? '' : role,
        };
    });
}

function fromDraftRows(rows) {
    return rows.map((r) => ({
        role: r.roleMode === CUSTOM ? String(r.customRole || '').trim() : r.roleMode,
        content: String(r.content || '').trim(),
    }));
}

const ChatContextPanel = observer(() => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const chatId = chatStore.contextPanelChatId;
    const open = !!chatId;
    const chat = chatStore.chats.find((c) => c.id === chatId) || null;

    const [rows, setRows] = useState([blankRow()]);
    const [saving, setSaving] = useState(false);
    const [templateId, setTemplateId] = useState(null);
    const [templateName, setTemplateName] = useState('');

    const templates = chatStore.settings.context_templates || [];

    useEffect(() => {
        if (!open || !chatId) return;
        const current = chatStore.chats.find((c) => c.id === chatId);
        setRows(toDraftRows(current?.context_messages));
        setTemplateId(null);
        setTemplateName('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, chatId]);

    const title = useMemo(
        () => (chat ? `Контекст: ${chat.title || 'Чат'}` : 'Контекст чата'),
        [chat]
    );

    const updateRow = (index, patch) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const moveRow = (index, dir) => {
        setRows((prev) => {
            const next = [...prev];
            const j = index + dir;
            if (j < 0 || j >= next.length) return prev;
            [next[index], next[j]] = [next[j], next[index]];
            return next;
        });
    };

    const removeRow = (index) => {
        setRows((prev) => (prev.length <= 1 ? [blankRow()] : prev.filter((_, i) => i !== index)));
    };

    const onSave = async () => {
        setSaving(true);
        try {
            await chatStore.saveChatContext(chatId, fromDraftRows(rows));
            antdMessage.success('Контекст сохранён');
            chatStore.closeContextPanel();
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось сохранить');
        } finally {
            setSaving(false);
        }
    };

    const onLoadTemplate = async () => {
        if (!templateId) {
            antdMessage.info('Выберите шаблон');
            return;
        }
        const tpl = templates.find((t) => t.id === templateId);
        if (!tpl) return;
        setRows(toDraftRows(tpl.messages));
        antdMessage.success(`Загружен шаблон «${tpl.name}»`);
    };

    const onSaveTemplate = async () => {
        const name = templateName.trim();
        if (!name) {
            antdMessage.info('Введите название шаблона');
            return;
        }
        try {
            const id = await chatStore.saveContextTemplate(name, fromDraftRows(rows));
            setTemplateId(id);
            setTemplateName('');
            antdMessage.success('Шаблон сохранён');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось сохранить шаблон');
        }
    };

    const onDeleteTemplate = async () => {
        if (!templateId) return;
        try {
            await chatStore.deleteContextTemplate(templateId);
            setTemplateId(null);
            antdMessage.success('Шаблон удалён');
        } catch (e) {
            antdMessage.error(e.message || 'Не удалось удалить шаблон');
        }
    };

    return (
        <Modal
            title={title}
            open={open}
            onCancel={() => chatStore.closeContextPanel()}
            width={720}
            destroyOnClose
            footer={[
                <Button key="cancel" onClick={() => chatStore.closeContextPanel()}>
                    Отмена
                </Button>,
                <Button key="save" type="primary" loading={saving} onClick={onSave}>
                    Сохранить в чат
                </Button>,
            ]}
        >
            <div className={`space-y-4 ${isDark ? 'text-gray-100' : ''}`}>
                <p className="text-xs opacity-60">
                    Сообщения контекста добавляются в начало запроса к модели (до истории чата).
                    Роль: system / user / assistant / tool или своя.
                </p>

                <div
                    className={`rounded-xl border p-3 ${
                        isDark ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/[0.02]'
                    }`}
                >
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide opacity-60">
                        Шаблоны
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Select
                            className="min-w-0 flex-1"
                            placeholder="Выберите шаблон"
                            allowClear
                            value={templateId}
                            onChange={(v) => setTemplateId(v || null)}
                            options={templates.map((t) => ({ value: t.id, label: t.name }))}
                        />
                        <Button onClick={onLoadTemplate} disabled={!templateId}>
                            Загрузить
                        </Button>
                        <Button danger onClick={onDeleteTemplate} disabled={!templateId}>
                            Удалить
                        </Button>
                    </div>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                        <Input
                            className="min-w-0 flex-1"
                            placeholder="Название нового шаблона"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                        />
                        <Button onClick={onSaveTemplate}>Сохранить как шаблон</Button>
                    </div>
                </div>

                <div className="space-y-3">
                    {rows.map((row, index) => (
                        <div
                            key={index}
                            className={`rounded-xl border p-3 ${
                                isDark ? 'border-white/10' : 'border-black/10'
                            }`}
                        >
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <Select
                                    style={{ minWidth: 140 }}
                                    value={row.roleMode}
                                    onChange={(v) => {
                                        if (v === CUSTOM) {
                                            updateRow(index, {
                                                roleMode: CUSTOM,
                                                customRole: row.customRole || '',
                                            });
                                        } else {
                                            updateRow(index, { roleMode: v, role: v });
                                        }
                                    }}
                                    options={[
                                        ...ROLE_PRESETS.map((r) => ({ value: r, label: r })),
                                        { value: CUSTOM, label: 'Своя…' },
                                    ]}
                                />
                                {row.roleMode === CUSTOM && (
                                    <Input
                                        style={{ maxWidth: 160 }}
                                        placeholder="role"
                                        value={row.customRole}
                                        onChange={(e) =>
                                            updateRow(index, { customRole: e.target.value })
                                        }
                                    />
                                )}
                                <div className="ml-auto flex gap-1">
                                    <Button size="small" onClick={() => moveRow(index, -1)} disabled={index === 0}>
                                        ↑
                                    </Button>
                                    <Button
                                        size="small"
                                        onClick={() => moveRow(index, 1)}
                                        disabled={index === rows.length - 1}
                                    >
                                        ↓
                                    </Button>
                                    <Button size="small" danger onClick={() => removeRow(index)}>
                                        Удалить
                                    </Button>
                                </div>
                            </div>
                            <Input.TextArea
                                rows={3}
                                placeholder="content"
                                value={row.content}
                                onChange={(e) => updateRow(index, { content: e.target.value })}
                            />
                        </div>
                    ))}
                </div>

                <Button type="dashed" block onClick={() => setRows((prev) => [...prev, blankRow()])}>
                    + Добавить сообщение
                </Button>
            </div>
        </Modal>
    );
});

export default ChatContextPanel;
